import { useRef, useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import {
  ArrowLeft,
  Download,
  Upload,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  Pencil,
  Trash2,
  Plus,
} from "lucide-react";
import type {
  RecommendedFurnishing,
  RecommendedFurnishingRoomType,
  RecommendedFurnishingClickStats,
  RecommendedFurnishingClickTimeseriesBucket,
} from "@shared/schema";
import { RECOMMENDED_FURNISHING_ROOM_TYPE_LABELS, RECOMMENDED_FURNISHING_ROOM_TYPES } from "@shared/schema";
import { usePageTitle } from "@/hooks/use-page-title";
import { AdminAccessGuard } from "@/components/admin-access-guard";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";

interface ImportError {
  row: number;
  message: string;
}

function formatPrice(priceCents: number): string {
  if (priceCents === 0) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    priceCents / 100,
  );
}

// Task #1788 — Build a fixed-length array of weekly click counts ending on the
// most recent (current) week. weekStart strings come from the server as
// 'YYYY-MM-DD' (Monday, UTC) via Postgres date_trunc('week', ...).
function buildWeeklySeries(
  buckets: RecommendedFurnishingClickTimeseriesBucket[],
  weeks: number,
): number[] {
  // Compute current week start (Monday, UTC) to align with Postgres date_trunc('week').
  const now = new Date();
  const utcDay = now.getUTCDay(); // 0=Sun..6=Sat
  const daysSinceMonday = (utcDay + 6) % 7;
  const currentWeekStart = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - daysSinceMonday),
  );
  const labels: string[] = [];
  for (let i = weeks - 1; i >= 0; i--) {
    const d = new Date(currentWeekStart);
    d.setUTCDate(d.getUTCDate() - i * 7);
    labels.push(d.toISOString().slice(0, 10));
  }
  const byLabel = new Map<string, number>();
  for (const b of buckets) byLabel.set(b.weekStart, (byLabel.get(b.weekStart) ?? 0) + b.clicks);
  return labels.map((l) => byLabel.get(l) ?? 0);
}

function Sparkline({ data, width = 96, height = 24 }: { data: number[]; width?: number; height?: number }) {
  if (!data || data.length === 0 || data.every((v) => v === 0)) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  const max = Math.max(1, ...data);
  const stepX = data.length > 1 ? width / (data.length - 1) : 0;
  const points = data
    .map((v, i) => {
      const x = i * stepX;
      const y = height - (v / max) * (height - 2) - 1;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
  const lastVal = data[data.length - 1];
  const lastX = (data.length - 1) * stepX;
  const lastY = height - (lastVal / max) * (height - 2) - 1;
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="inline-block align-middle text-primary"
      aria-hidden="true"
    >
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
      <circle cx={lastX} cy={lastY} r={1.75} fill="currentColor" />
    </svg>
  );
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

export default function AppAdminRecommendedFurnishings() {
  const queryClient = useQueryClient();
  usePageTitle("Recommended Furnishings");
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingRowCount, setPendingRowCount] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [importErrors, setImportErrors] = useState<ImportError[]>([]);
  const [importSuccess, setImportSuccess] = useState<number | null>(null);

  type DialogMode = { kind: "edit"; item: RecommendedFurnishing } | { kind: "add" } | null;
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [editForm, setEditForm] = useState<{
    roomType: RecommendedFurnishingRoomType;
    name: string;
    description: string;
    priceUsd: string;
    purchaseUrl: string;
    sortOrder: string;
  }>({ roomType: "bedroom", name: "", description: "", priceUsd: "", purchaseUrl: "", sortOrder: "0" });
  const [editError, setEditError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<RecommendedFurnishing | null>(null);

  function openEditDialog(item: RecommendedFurnishing) {
    setDialogMode({ kind: "edit", item });
    setEditError(null);
    setEditForm({
      roomType: (item.roomType as RecommendedFurnishingRoomType) ?? "bedroom",
      name: item.name,
      description: item.description ?? "",
      priceUsd: item.priceCents > 0 ? (item.priceCents / 100).toFixed(2) : "",
      purchaseUrl: item.purchaseUrl ?? "",
      sortOrder: String(item.sortOrder ?? 0),
    });
  }

  function openAddDialog() {
    setDialogMode({ kind: "add" });
    setEditError(null);
    setEditForm({ roomType: "bedroom", name: "", description: "", priceUsd: "", purchaseUrl: "", sortOrder: "0" });
  }

  function closeDialog() {
    setDialogMode(null);
    setEditError(null);
  }

  const updateMutation = useMutation({
    mutationFn: async (vars: { id: number; patch: Record<string, unknown> }) => {
      const res = await apiRequest("PATCH", `/api/admin/recommended-furnishings/${vars.id}`, vars.patch);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? "Update failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/public/recommended-furnishings"] });
      closeDialog();
      toast({ title: "Item updated" });
    },
    onError: (err: any) => {
      setEditError(parseErrorMessage(err, "Unknown error"));
    },
  });

  const createMutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const res = await apiRequest("POST", "/api/admin/recommended-furnishings", body);
      if (!res.ok) {
        const responseBody = await res.json().catch(() => ({}));
        throw new Error(responseBody.message ?? "Create failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/public/recommended-furnishings"] });
      closeDialog();
      toast({ title: "Item added" });
    },
    onError: (err: any) => {
      setEditError(parseErrorMessage(err, "Unknown error"));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("DELETE", `/api/admin/recommended-furnishings/${id}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? "Delete failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/public/recommended-furnishings"] });
      setDeleteTarget(null);
      toast({ title: "Item deleted" });
    },
    onError: (err: any) => {
      toast({
        title: "Delete failed",
        description: parseErrorMessage(err, "Unknown error"),
        variant: "destructive",
      });
    },
  });

  function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!dialogMode) return;
    const name = editForm.name.trim();
    if (!name) {
      setEditError("Name is required");
      return;
    }
    const priceStr = editForm.priceUsd.trim().replace(/[$,\s]/g, "");
    let priceCents = 0;
    if (priceStr !== "") {
      const priceNum = parseFloat(priceStr);
      if (!Number.isFinite(priceNum) || priceNum < 0) {
        setEditError("Price must be a non-negative number");
        return;
      }
      priceCents = Math.round(priceNum * 100);
    }
    const sortOrder = parseInt(editForm.sortOrder, 10);
    if (!Number.isFinite(sortOrder)) {
      setEditError("Sort order must be an integer");
      return;
    }
    const purchaseUrl = editForm.purchaseUrl.trim();
    const description = editForm.description.trim();
    const payload = {
      roomType: editForm.roomType,
      name,
      description: description === "" ? null : description,
      priceCents,
      purchaseUrl: purchaseUrl === "" ? null : purchaseUrl,
      sortOrder,
    };
    if (dialogMode.kind === "edit") {
      updateMutation.mutate({ id: dialogMode.item.id, patch: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  const { data: items, isLoading } = useQuery<RecommendedFurnishing[]>({
    queryKey: ["/api/public/recommended-furnishings"],
  });

  // Task #1777 — Per-item click stats for revenue measurement.
  const { data: clickStats, error: clickStatsError } = useQuery<RecommendedFurnishingClickStats[]>({
    queryKey: ["/api/admin/recommended-furnishings/click-stats"],
  });
  const clickStatsById = new Map<number, RecommendedFurnishingClickStats>(
    (clickStats ?? []).map((s) => [s.furnishingId, s]),
  );

  // Task #1788 — Weekly click time-series per item (last 12 weeks) for sparklines.
  const TREND_WEEKS = 12;
  const { data: clickTimeseries } = useQuery<{
    weeks: number;
    buckets: RecommendedFurnishingClickTimeseriesBucket[];
  }>({
    // Keep the URL as a single queryKey segment so the default queryFn
    // (which fetches `queryKey.join("/")`) hits the endpoint with the
    // expected `?weeks=` query string instead of treating the number as
    // a path segment.
    queryKey: [`/api/admin/recommended-furnishings/click-stats/timeseries?weeks=${TREND_WEEKS}`],
  });
  const seriesByFurnishingId = new Map<number, number[]>();
  if (clickTimeseries?.buckets?.length) {
    const grouped = new Map<number, RecommendedFurnishingClickTimeseriesBucket[]>();
    for (const b of clickTimeseries.buckets) {
      const arr = grouped.get(b.furnishingId) ?? [];
      arr.push(b);
      grouped.set(b.furnishingId, arr);
    }
    for (const [id, bs] of grouped) {
      seriesByFurnishingId.set(id, buildWeeklySeries(bs, TREND_WEEKS));
    }
  }

  // Export
  function handleExport() {
    window.location.href = "/api/admin/recommended-furnishings/export";
  }

  // Import — step 1: pick file, do a client-side row count for the confirm summary
  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
    setImportErrors([]);
    setImportSuccess(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    // Count non-empty data rows (skip header at index 0) using xlsx
    let rowCount: number | null = null;
    try {
      const XLSX = await import("xlsx");
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheetName = wb.SheetNames[0];
      if (sheetName) {
        const ws = wb.Sheets[sheetName];
        const allRows = (XLSX.utils.sheet_to_json(ws, { header: 1 }) as unknown[]) as unknown[][];
        rowCount = allRows.slice(1).filter((r) => (r as unknown[]).some((c) => c != null && String(c).trim() !== "")).length;
      }
    } catch {
      rowCount = null;
    }
    setPendingRowCount(rowCount);
    setConfirmOpen(true);
  }

  // Import — step 2: confirm + submit
  const importMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (file: File): Promise<{ imported: number }> => {
      const xlsxBase64 = await fileToBase64(file);
      const res = await apiRequest("POST", "/api/admin/recommended-furnishings/import", {
        xlsxBase64,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (body.errors) throw Object.assign(new Error(body.message ?? "Validation failed"), { errors: body.errors });
        throw new Error(body.message ?? "Import failed");
      }
      return res.json();
    },
    onSuccess: (result) => {
      setConfirmOpen(false);
      setPendingFile(null);
      setPendingRowCount(null);
      setImportSuccess(result.imported);
      setImportErrors([]);
      queryClient.invalidateQueries({ queryKey: ["/api/public/recommended-furnishings"] });
      toast({ title: "Import complete", description: `${result.imported} furnishing items are now live.` });
    },
    onError: (err: any) => {
      setConfirmOpen(false);
      if (err.errors) {
        setImportErrors(err.errors);
      } else {
        toast({
          title: "Import failed",
          description: parseErrorMessage(err, "Unknown error"),
          variant: "destructive",
        });
      }
    },
  });

  function handleConfirmImport() {
    if (!pendingFile) return;
    importMutation.mutate(pendingFile);
  }

  const currentCount = items?.length ?? 0;

  return (
    <AdminAccessGuard error={clickStatsError}>
    <div className="container mx-auto max-w-5xl p-6 space-y-6" data-testid="page-admin-furnishings" data-tour="admin-recommended-furnishings-section">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link href="/admin">
            <Button variant="ghost" size="sm" data-testid="link-back-admin">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to admin
            </Button>
          </Link>
          <h1 className="text-2xl font-bold mt-2" data-testid="text-page-title">
            Recommended Furnishings
          </h1>
          <p className="text-sm text-muted-foreground max-w-xl">
            Manage the curated furnishings list shown on the public{" "}
            <a
              href="/recommendedfurnishings"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline inline-flex items-center gap-1"
              data-testid="link-public-page"
            >
              /recommendedfurnishings <ExternalLink className="h-3 w-3" />
            </a>{" "}
            page. Use XLSX export/import to update the list in bulk.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <GuideButton pageKey="admin-recommended-furnishings" compactOnMobile />
          <Button
            variant="outline"
            onClick={handleExport}
            data-testid="button-export"
            disabled={currentCount === 0}
          >
            <Download className="mr-2 h-4 w-4" /> Export XLSX
          </Button>
          <Button
            variant="outline"
            onClick={openAddDialog}
            data-testid="button-add-item"
          >
            <Plus className="mr-2 h-4 w-4" /> Add item
          </Button>
          <Button
            onClick={() => fileInputRef.current?.click()}
            data-testid="button-import"
            disabled={importMutation.isPending}
          >
            {importMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-2 h-4 w-4" />
            )}
            Import XLSX
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleFileChange}
            className="hidden"
            data-testid="input-xlsx-file"
          />
        </div>
      </div>
      <GuideNudgeBanner pageKey="admin-recommended-furnishings" />

      {/* Import success alert */}
      {importSuccess !== null && (
        <Alert data-testid="alert-import-success">
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Import complete</AlertTitle>
          <AlertDescription>
            {importSuccess} item{importSuccess === 1 ? "" : "s"} are now live on the public page.
          </AlertDescription>
        </Alert>
      )}

      {/* Import errors */}
      {importErrors.length > 0 && (
        <Alert variant="destructive" data-testid="alert-import-errors">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Validation errors — nothing was imported</AlertTitle>
          <AlertDescription>
            <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
              {importErrors.map((e, i) => (
                <li key={i}>
                  Row {e.row}: {e.message}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* XLSX format guide */}
      <Card data-testid="card-format-guide">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">XLSX Format</CardTitle>
          <CardDescription className="text-xs">
            The first row must be a header row. Subsequent rows are data. Columns (in order):
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="text-sm space-y-1 list-decimal list-inside text-muted-foreground">
            <li><strong>Room Type</strong> — one of: bedroom, bathroom, kitchen, living_room, common_area, exterior</li>
            <li><strong>Item Name</strong> — required, non-empty string</li>
            <li><strong>Description</strong> — optional text describing the item</li>
            <li><strong>Price (USD)</strong> — numeric value like 29.99 or $29.99 (0 = price not listed)</li>
            <li><strong>Purchase URL</strong> — optional valid https:// link to buy the item</li>
            <li><strong>Sort Order</strong> — integer controlling display order (optional, defaults to 0)</li>
            <li><strong>Affiliate URL</strong> — optional valid https:// affiliate or UTM-tracked link. When present, the public page links here instead of the Purchase URL so referral revenue can be attributed.</li>
          </ol>
        </CardContent>
      </Card>

      {/* Current list */}
      <Card data-testid="card-current-list">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              Current List
              {!isLoading && (
                <Badge variant="secondary" className="ml-2" data-testid="badge-item-count">
                  {currentCount} item{currentCount === 1 ? "" : "s"}
                </Badge>
              )}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3" data-testid="skeleton-list">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : currentCount === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm" data-testid="text-empty">
              No furnishings yet. Import an XLSX file to populate the list.
            </div>
          ) : (
            <div className="border rounded-md overflow-hidden">
              <Table data-testid="table-furnishings">
                <TableHeader>
                  <TableRow>
                    <TableHead>Room Type</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead>Link</TableHead>
                    <TableHead>Affiliate</TableHead>
                    <TableHead className="text-right">Clicks</TableHead>
                    <TableHead>Trend (12w)</TableHead>
                    <TableHead className="text-right">Sort</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(items ?? []).map((item) => (
                    <TableRow key={item.id} data-testid={`row-furnishing-${item.id}`}>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {RECOMMENDED_FURNISHING_ROOM_TYPE_LABELS[item.roomType as keyof typeof RECOMMENDED_FURNISHING_ROOM_TYPE_LABELS] ?? item.roomType}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium text-sm">{item.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {item.description ?? "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {formatPrice(item.priceCents)}
                      </TableCell>
                      <TableCell>
                        {item.purchaseUrl ? (
                          <a
                            href={item.purchaseUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline text-xs inline-flex items-center gap-1"
                            data-testid={`link-purchase-${item.id}`}
                          >
                            Link <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.affiliateUrl ? (
                          <div className="flex items-center gap-2">
                            <Badge variant="default" className="text-xs" data-testid={`badge-affiliate-active-${item.id}`}>
                              Tracking
                            </Badge>
                            <a
                              href={item.affiliateUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline text-xs inline-flex items-center gap-1"
                              data-testid={`link-affiliate-${item.id}`}
                              title={item.affiliateUrl}
                            >
                              Link <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm" data-testid={`text-clicks-${item.id}`}>
                        {(() => {
                          const s = clickStatsById.get(item.id);
                          const total = s?.totalClicks ?? 0;
                          const last30 = s?.clicks30d ?? 0;
                          if (total === 0) {
                            return <span className="text-muted-foreground">0</span>;
                          }
                          return (
                            <span className="tabular-nums" title={`${last30} in last 30d`}>
                              {total}
                              <span className="text-muted-foreground text-xs ml-1">({last30}/30d)</span>
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell data-testid={`sparkline-${item.id}`}>
                        {(() => {
                          const series = seriesByFurnishingId.get(item.id);
                          if (!series) {
                            return <span className="text-muted-foreground text-xs">—</span>;
                          }
                          const total = series.reduce((a, b) => a + b, 0);
                          return (
                            <span
                              className="inline-flex items-center"
                              title={`Weekly clicks (last ${TREND_WEEKS}w): ${series.join(", ")}`}
                            >
                              <Sparkline data={series} />
                              <span className="sr-only">{total} clicks in last {TREND_WEEKS} weeks</span>
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">
                        {item.sortOrder}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(item)}
                            data-testid={`button-edit-${item.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                            <span className="sr-only">Edit</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteTarget(item)}
                            data-testid={`button-delete-${item.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                            <span className="sr-only">Delete</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit item dialog */}
      <Dialog open={dialogMode !== null} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="dialog-edit-furnishing">
          <form onSubmit={handleEditSubmit}>
            <DialogHeader>
              <DialogTitle>{dialogMode?.kind === "add" ? "Add furnishing" : "Edit furnishing"}</DialogTitle>
              <DialogDescription>
                {dialogMode?.kind === "add"
                  ? "Add a new item to the recommended furnishings list."
                  : "Update this single item without re-importing the whole list."}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-room-type">Room Type</Label>
                <Select
                  value={editForm.roomType}
                  onValueChange={(v) => setEditForm((f) => ({ ...f, roomType: v as RecommendedFurnishingRoomType }))}
                >
                  <SelectTrigger id="edit-room-type" data-testid="select-edit-room-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECOMMENDED_FURNISHING_ROOM_TYPES.map((rt) => (
                      <SelectItem key={rt} value={rt}>
                        {RECOMMENDED_FURNISHING_ROOM_TYPE_LABELS[rt]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="input-edit-name"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  rows={3}
                  value={editForm.description}
                  onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                  data-testid="input-edit-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-2">
                  <Label htmlFor="edit-price">Price (USD)</Label>
                  <Input
                    id="edit-price"
                    inputMode="decimal"
                    placeholder="29.99"
                    value={editForm.priceUsd}
                    onChange={(e) => setEditForm((f) => ({ ...f, priceUsd: e.target.value }))}
                    data-testid="input-edit-price"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-sort">Sort Order</Label>
                  <Input
                    id="edit-sort"
                    inputMode="numeric"
                    value={editForm.sortOrder}
                    onChange={(e) => setEditForm((f) => ({ ...f, sortOrder: e.target.value }))}
                    data-testid="input-edit-sort"
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-url">Purchase URL</Label>
                <Input
                  id="edit-url"
                  type="url"
                  placeholder="https://…"
                  value={editForm.purchaseUrl}
                  onChange={(e) => setEditForm((f) => ({ ...f, purchaseUrl: e.target.value }))}
                  data-testid="input-edit-url"
                />
              </div>
              {editError && (
                <p className="text-sm text-destructive" data-testid="text-edit-error">{editError}</p>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeDialog}
                data-testid="button-cancel-edit"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateMutation.isPending || createMutation.isPending}
                data-testid="button-save-edit"
              >
                {(updateMutation.isPending || createMutation.isPending) ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {dialogMode?.kind === "add" ? "Adding…" : "Saving…"}</>
                ) : (
                  dialogMode?.kind === "add" ? "Add item" : "Save changes"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
      >
        <AlertDialogContent data-testid="dialog-confirm-delete">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this furnishing?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget ? (
                <>This will permanently remove <strong>{deleteTarget.name}</strong> from the recommended furnishings list. This cannot be undone.</>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
              }}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deleting…</>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm import dialog */}
      <Dialog open={confirmOpen} onOpenChange={(o) => { if (!o) { setConfirmOpen(false); setPendingFile(null); setPendingRowCount(null); } }}>
        <DialogContent data-testid="dialog-confirm-import">
          <DialogHeader>
            <DialogTitle>Confirm Import</DialogTitle>
            <DialogDescription>
              Importing <strong>{pendingFile?.name}</strong> will{" "}
              <strong>
                {pendingRowCount !== null
                  ? `replace all ${currentCount} current item${currentCount === 1 ? "" : "s"} with ${pendingRowCount} row${pendingRowCount === 1 ? "" : "s"} from this file`
                  : `replace all ${currentCount} current item${currentCount === 1 ? "" : "s"} with the rows from this file`}
              </strong>. This cannot be undone. Export a backup first if needed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setConfirmOpen(false); setPendingFile(null); }}
              data-testid="button-cancel-import"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmImport}
              disabled={importMutation.isPending}
              data-testid="button-confirm-import"
            >
              {importMutation.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Importing…</>
              ) : (
                "Yes, replace list"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </AdminAccessGuard>
  );
}
